import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  Input,
  Textarea,
} from "@aircall/ds";
import { useForm } from "@tanstack/react-form";
import { TFunction, useTranslation } from "react-i18next";

import { ScorecardLanguageSelect } from "../ScorecardLanguageSelect";

import { ScorecardPreCreateFormValue, ScorecardPreCreateModalProps } from ".";

export const validateTitle = (value: string, t: TFunction) => {
  if (!value.trim().length) {
    return t("score_card.required_message");
  }
  return undefined;
};

// Render a modal to fill the name, description and select the language of the scorecard,
// When submitted, we redirect to the new scorecard page with the data filled
export function ScorecardPreCreateModal({
  show,
  onHide,
  onSubmit,
}: ScorecardPreCreateModalProps) {
  const { t } = useTranslation();
  const form = useForm({
    defaultValues: {
      name: "",
      description: "",
      language: "en",
    } as ScorecardPreCreateFormValue,
    onSubmit: ({ value }) => {
      onSubmit(value);
    },
  });

  return (
    <Dialog
      open={show}
      onOpenChange={(open) => {
        if (!open) {
          onHide();
        }
      }}
    >
      <DialogContent
        className="sm:max-w-md"
        data-test="pre-create-scorecard-modal"
        showCloseButton={false}
      >
        <DialogHeader>
          <DialogTitle>{t("score_card.new_scorecard")}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <div className="flex flex-col gap-4">
            <form.Field
              name="name"
              validators={{ onChange: ({ value }) => validateTitle(value, t) }}
            >
              {(nameField) => {
                const error = nameField.state.meta.errorMap.onChange;
                return (
                  <Field>
                    <FieldLabel htmlFor="pre-create-scorecard-name-input">
                      {t("score_card.name")}
                    </FieldLabel>
                    <Input
                      id="pre-create-scorecard-name-input"
                      value={nameField.state.value}
                      onChange={(e) => nameField.handleChange(e.target.value)}
                      data-test="pre-create-scorecard-name-input"
                      aria-invalid={error ? true : undefined}
                    />
                    {error ? (
                      <FieldError errors={[{ message: String(error) }]} />
                    ) : null}
                  </Field>
                );
              }}
            </form.Field>
            <form.Field name="description">
              {(descriptionField) => (
                <Field>
                  <FieldLabel htmlFor="pre-create-scorecard-description-input">
                    {t("score_card.description")}
                  </FieldLabel>
                  <Textarea
                    id="pre-create-scorecard-description-input"
                    value={descriptionField.state.value}
                    onChange={(e) => {
                      descriptionField.handleChange(e.target.value);
                    }}
                    data-test="pre-create-scorecard-description-input"
                  />
                  <FieldDescription>{t("optional")}</FieldDescription>
                </Field>
              )}
            </form.Field>
            <form.Field name="language">
              {(languageField) => (
                <Field>
                  <FieldLabel>{t("score_card.language")}</FieldLabel>
                  <ScorecardLanguageSelect
                    onChange={(v) => {
                      languageField.handleChange(v);
                    }}
                    value={languageField.state.value}
                    data-test="pre-create-scorecard-select-language"
                  />
                </Field>
              )}
            </form.Field>
          </div>
          <DialogFooter className="mt-4">
            <Button
              variant="ghost"
              size="lg"
              type="button"
              onClick={onHide}
              data-test="pre-create-scorecard-cancel-button"
            >
              {t("cancel")}
            </Button>
            <form.Subscribe selector={(state) => [state.canSubmit]}>
              {([canSubmit]) => (
                <Button
                  variant="secondary"
                  size="lg"
                  type="submit"
                  disabled={!canSubmit}
                  data-test="pre-create-scorecard-create-button"
                >
                  {t("create")}
                </Button>
              )}
            </form.Subscribe>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
